import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/auth-context';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert, IconButton,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Tooltip, Stack, Card, CardContent,
} from '@mui/material';
import EditIcon        from '@mui/icons-material/Edit';
import LockResetIcon   from '@mui/icons-material/LockReset';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileUploadIcon  from '@mui/icons-material/FileUpload';
import {
  useGetDirectorsQuery,
  useUpdateDirectorMutation,
  useAdminResetPasswordMutation,
} from '../../redux/services/auth-api';
import { useIsMobile } from '../../hooks/use-is-mobile';

// ── Edit Dialog ───────────────────────────────────────────────────────────────
function EditDialog({ director, onClose }) {
  const [fullname, setFullname] = useState(director.fullname);
  const [username, setUsername] = useState(director.username);
  const [email,    setEmail]    = useState(director.email || '');
  const [updateDirector, { isLoading, error }] = useUpdateDirectorMutation();
  const isMobile = useIsMobile();

  const handleSave = async () => {
    try {
      await updateDirector({ id: director._id, fullname, username, email: email || undefined }).unwrap();
      onClose();
    } catch (_) {}
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ bgcolor: '#284B32', color: '#fff', py: 1.5 }}>
        Editar director
      </DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {error && (
          <Alert severity="error">
            {error.data?.message || 'Error al guardar'}
          </Alert>
        )}
        <TextField
          label="Nombre completo"
          value={fullname}
          onChange={e => setFullname(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Usuario"
          value={username}
          onChange={e => setUsername(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Correo"
          value={email}
          onChange={e => setEmail(e.target.value)}
          size="small"
          fullWidth
          type="email"
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || !fullname.trim() || !username.trim()}
          sx={{ bgcolor: '#284B32', '&:hover': { bgcolor: '#1a3322' } }}
        >
          {isLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Reset Password Dialog ─────────────────────────────────────────────────────
function ResetDialog({ director, onClose }) {
  const [tempPassword, setTempPassword]       = useState(null);
  const [copied, setCopied]                   = useState(false);
  const [resetPassword, { isLoading, error }] = useAdminResetPasswordMutation();
  const isMobile = useIsMobile();

  const handleConfirm = async () => {
    try {
      const res = await resetPassword(director._id).unwrap();
      setTempPassword(res.tempPassword);
    } catch (_) {}
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ bgcolor: '#284B32', color: '#fff', py: 1.5 }}>
        Restablecer contraseña
      </DialogTitle>
      <DialogContent sx={{ pt: 2, mt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.data?.message || 'Error al restablecer'}
          </Alert>
        )}
        {!tempPassword ? (
          <Typography variant="body2">
            Se generará una contraseña temporal para <strong>{director.fullname}</strong>.
            El director deberá cambiarla al iniciar sesión.
          </Typography>
        ) : (
          <Box>
            <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
              Esta contraseña solo se muestra una vez. Anótala antes de cerrar.
            </Alert>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#f4f1e9', borderRadius: 1, px: 2, py: 1.5,
            }}>
              <Typography
                sx={{ fontFamily: 'monospace', fontSize: 18, flexGrow: 1, letterSpacing: 2 }}
              >
                {tempPassword}
              </Typography>
              <Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        {!tempPassword ? (
          <>
            <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isLoading}
              sx={{ bgcolor: '#284B32', '&:hover': { bgcolor: '#1a3322' } }}
            >
              {isLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Confirmar'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ bgcolor: '#284B32', '&:hover': { bgcolor: '#1a3322' } }}
          >
            Cerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DirectorsPage() {
  const { activeCamp } = useAuthContext();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { data: directors = [], isLoading, error } = useGetDirectorsQuery(
    activeCamp ? { campId: activeCamp._id } : {},
    { skip: !activeCamp }
  );
  const [editTarget,  setEditTarget]  = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700} color="#284B32">
          Directores
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileUploadIcon />}
          onClick={() => navigate('/directors/import')}
          disabled={!activeCamp}
          sx={{ textTransform: 'none', borderColor: '#284B32', color: '#284B32', '&:hover': { bgcolor: '#284B3210' } }}
        >
          Importar
        </Button>
      </Box>

      {!activeCamp && (
        <Alert severity="info">Selecciona un campamento para ver los directores.</Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#284B32' }} />
        </Box>
      )}

      {error && (
        <Alert severity="error">Error al cargar directores</Alert>
      )}

      {/* Mobile: card list */}
      {activeCamp && !isLoading && !error && isMobile && (
        <Stack spacing={1.5}>
          {directors.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No hay directores registrados en este campamento
            </Typography>
          ) : directors.map(dir => (
            <Card key={dir._id} variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>{dir.fullname}</Typography>
                  {dir.tempPassword ? (
                    <Chip label="Temp. password" color="warning" size="small" />
                  ) : (
                    <Chip label="Activo" color="success" size="small" variant="outlined" />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                  @{dir.username}
                </Typography>
                {dir.email && (
                  <Typography variant="caption" color="text.secondary" display="block">{dir.email}</Typography>
                )}
                {dir.clubName && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Club: {dir.clubName}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                  <IconButton size="small" aria-label="Editar" onClick={() => setEditTarget(dir)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="Restablecer contraseña" onClick={() => setResetTarget(dir)} sx={{ color: '#B45309' }}>
                    <LockResetIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Desktop: table */}
      {activeCamp && !isLoading && !error && !isMobile && (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#284B32' }}>
                {['Nombre completo', 'Usuario', 'Correo', 'Club', 'Estado', 'Acciones'].map(h => (
                  <TableCell key={h} sx={{ color: '#fff', fontWeight: 600, py: 1.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {directors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#6b7280' }}>
                    No hay directores registrados en este campamento
                  </TableCell>
                </TableRow>
              ) : (
                directors.map((dir, idx) => (
                  <TableRow
                    key={dir._id}
                    sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#f9fafb', '&:hover': { bgcolor: '#f0fdf4' } }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{dir.fullname}</TableCell>
                    <TableCell sx={{ color: '#6b7280', fontFamily: 'monospace' }}>{dir.username}</TableCell>
                    <TableCell sx={{ color: '#6b7280' }}>{dir.email || '—'}</TableCell>
                    <TableCell>{dir.clubName || '—'}</TableCell>
                    <TableCell>
                      {dir.tempPassword ? (
                        <Chip label="Contraseña temporal" color="warning" size="small" />
                      ) : (
                        <Chip label="Activo" color="success" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => setEditTarget(dir)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Restablecer contraseña">
                        <IconButton size="small" onClick={() => setResetTarget(dir)} sx={{ color: '#B45309' }}>
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editTarget  && <EditDialog  director={editTarget}  onClose={() => setEditTarget(null)}  />}
      {resetTarget && <ResetDialog director={resetTarget} onClose={() => setResetTarget(null)} />}
    </Box>
  );
}
